import React from "react";
import { Link } from "react-router-dom";
import { Brain, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { ServiceCard } from "../components/ServiceCard";
import { NEUROLOGY_SERVICES, CLINIC } from "../lib/clinic";

const NeurologyServices = () => {
  return (
    <div data-testid="neurology-page">
      <PageHeader
        eyebrow="Neurology"
        title="Modern neurology with a personal touch."
        subtitle="From migraines to movement disorders — evidence-based diagnosis, careful prescribing and long-term follow-up for every patient."
        breadcrumbs={["Services", "Neurology"]}
      />

      <section className="section-y bg-paper">
        <div className="container-x">
          <div className="flex items-center gap-5 mb-16">
            <Brain className="w-7 h-7 text-brand-tealDark" strokeWidth={1.3} />
            <div>
              <div className="heading-serif text-2xl md:text-3xl font-medium text-brand-navyDark">
                Conditions we treat
              </div>
              <p className="text-[13px] text-brand-muted tracking-wide">All consultations start with a detailed history & examination.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200">
            {NEUROLOGY_SERVICES.map((s, i) => (
              <ServiceCard key={s.title} {...s} index={i} accent="sage" />
            ))}
          </div>
        </div>
      </section>

      <section className="section-y bg-paper-soft">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">What to expect</div>
              <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-6">
                Your first neurology visit.
              </h2>
              <p className="text-brand-body leading-[1.85] text-[15px]">
                A typical first consultation lasts 30–45 minutes and includes a detailed
                history, focused neurological examination, and a clear conversation about
                investigations and treatment options. Reports from outside hospitals are
                welcome.
              </p>
            </div>
            <ol className="lg:col-span-6 lg:col-start-7 space-y-1">
              {[
                "Detailed history of your symptoms & timeline",
                "Hands-on neurological examination",
                "Review of past investigations & medications",
                "Clear diagnosis and treatment plan",
                "Follow-up & ongoing support",
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-6 py-5 border-b border-stone-200 last:border-0"
                >
                  <span className="heading-serif text-2xl text-brand-tealDark font-medium leading-none w-8 shrink-0">
                    0{i + 1}
                  </span>
                  <span className="text-brand-navyDark text-[16px] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="section-y bg-paper">
        <div className="container-x max-w-2xl">
          <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark mb-6 leading-[1.08]">
            Worried about a neurological symptom?
          </h2>
          <p className="text-brand-body mb-10 text-[16px] leading-[1.8]">
            Don't wait. Early consultation often makes the biggest difference in neurology.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact#book" className="btn-primary">
              Book Neurology Consultation <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="btn-secondary">
              WhatsApp Quick Question
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NeurologyServices;
